import {DEFAULT_THEME} from './constants'
import axios from 'axios'
import {Config} from './Config'
import {extractProducts} from './extractProducts'

const getLocalDate = date => {
  // new Date(null) is the epoch, so a missing date used to render as 01 Jan 1970.
  if (date === null || date === undefined || date === '') return ''
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export const ALLOWED_ANALYZE_ROLES = ['Analyse', 'Respond', 'Owner']
export const ALLOWED_INSIGHTS_ROLES = [
  'Analyse',
  'Respond',
  'Owner',
  'Survey Administration',
]
export const ALLOWED_SURVEY_CREATION_ROLES = ['Survey_Administration', 'Owner']

/** Roles that can change live survey end date (Owner, Survey Administrator) */
export const ALLOWED_CHANGE_LIVE_SURVEY_END_DATE_ROLES = [
  'Survey_Administration',
  'Owner',
]

const canCreateSurvey = user => {
  let allowed = false
  ALLOWED_SURVEY_CREATION_ROLES.map(role => {
    if (user?.roles?.indexOf(role) > -1) allowed = true
  })
  return allowed
}

const canChangeLiveSurveyEndDate = user => {
  let allowed = false
  ALLOWED_CHANGE_LIVE_SURVEY_END_DATE_ROLES.map(role => {
    if (user?.roles?.indexOf(role) > -1) allowed = true
  })
  return allowed
}

const canAnalyze = user => {
  let allowed = false
  ALLOWED_ANALYZE_ROLES.map(role => {
    if (user?.roles?.indexOf(role) > -1) allowed = true
  })
  return allowed
}

const getMatSites = apiParams => {
  return apiParams?.schoolIds
}

const getProducts = async (schoolId, matId) => {
  let products = []
  let res = await axios({
    method: 'get',
    url:
      Config.ADMIN_BASE_URL +
      `/SchoolProducts/GetSchoolMatProducts?schoolId=${schoolId}&matId=${matId}`,
    headers: {'Content-Type': 'application/json'},
  })
  // The Admin-Api response nests the array under `.Products` (older responses were a flat array);
  // normalise before filtering so this doesn't throw on the object.
  const productList = extractProducts(res.data.data)
  if (productList.filter(x => x.Product == 2)?.length > 0) {
    products.push('students')
  }
  if (productList.filter(x => x.Product == 3)?.length > 0) {
    products.push('parents')
  }
  if (productList.filter(x => x.Product == 4)?.length > 0) {
    products.push('staff')
  }

  return products
}

const parseJwt = token => {
  var base64Url = token.split('.')[1]
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)
}

const getTheming = ctheme => {
  var theme = !ctheme ? DEFAULT_THEME : ctheme

  return {
    typography: {
      body1: {
        color: theme.textColor,
        fontFamily: theme.font,
        fontSize: 16,
      },
      h1: {
        color: theme.textColor,
        fontFamily: theme.font,
        fontWeight: 600,
        fontSize: 48,
      },
      h2: {
        color: theme.textColor,
        fontFamily: theme.font,
        fontWeight: 300,
        fontSize: 24,
      },
      h3: {
        color: theme.textColor,
        fontFamily: theme.font,
        fontSize: 18,
      },
    },
    components: {
      MuiSlider: {
        styleOverrides: {
          root: {
            color: theme.accentColor,

            '& .MuiGrid-root': {
              color: theme.textColor,
              backgroundColor: theme.backgroundColor,
            },
            '& .MuiSlider-thumb': {
              backgroundColor: theme.accentColor,
              color: theme.accentColor,
            },
            '& .MuiSlider-track': {
              backgroundColor: theme.accentColor,
              color: theme.accentColor,
            },
            '& .MuiSlider-rail': {
              backgroundColor: theme.accentColor,
              color: theme.accentColor,
            },
            '& .MuiSlider-mark': {
              backgroundColor: theme.accentColor,
              color: theme.accentColor,
            },
            '& .MuiSlider-markLabel': {
              color: 'red',
              fontFamily: theme.font,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            backgroundColor: theme.answerColor,
            margin: '5px 0',
          },
          label: {
            backgroundColor: theme.answerColor,
            color: theme.textColor,
            fontFamily: theme.font,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            backgroundColor: theme.buttonColor,
            color: theme.buttonTextColor,
            borderColor: theme.buttonColor,
            ':hover': {
              backgroundColor: theme.buttonColor,
              borderColor: theme.buttonColor,
            },
            '&.MuiButton-outlined': {
              backgroundColor: 'transparent',
              borderColor: theme.buttonColor,
              color: theme.buttonColor,
              ':hover': {
                borderColor: theme.buttonColor,
                color: theme.buttonColor,
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            borderColor: theme.accentColor,
            color: theme.accentColor,
            '&.Mui-checked': {
              color: theme.accentColor,
            },
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              color: theme.textColor,
              backgroundColor: theme.backgroundColor,
            },
            '& .MuiFormLabel-root': {
              color: theme.textColor,
            },
            '& .MuiInputBase-input': {
              color: theme.textColor,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.accentColor,
            },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          root: {
            color: theme.textColor,
            backgroundColor: theme.backgroundColor,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.accentColor,
            },
            '& .MuiSvgIcon-root': {
              color: theme.accentColor,
            },
          },
          select: {
            color: theme.textColor,
            backgroundColor: theme.backgroundColor,
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: theme.textColor,
            '&.Mui-focused': {
              color: theme.accentColor,
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.accentColor,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.accentColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.accentColor,
            },
          },
          input: {
            color: theme.textColor,
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: theme.textColor,
            backgroundColor: theme.backgroundColor,
            '&:hover': {
              backgroundColor: theme.answerColor,
            },
            '&.Mui-selected': {
              backgroundColor: theme.answerColor,
              '&:hover': {
                backgroundColor: theme.answerColor,
              },
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: theme.backgroundColor,
            color: theme.textColor,
            border: `1px solid ${theme.accentColor}`,
            borderRadius: '8px',
          },
        },
      },

      MuiRadio: {
        styleOverrides: {
          root: {
            borderColor: theme.accentColor,
            color: theme.accentColor,
            '&.Mui-checked': {
              color: theme.accentColor,
            },
          },
        },
      },

      MuiBox: {
        styleOverrides: {
          root: {
            border: `1px solid ${theme.accentColor}`,
            borderRadius: '8px',
            '&.themed-box': {
              color: theme.textColor,
              backgroundColor: theme.backgroundColor,
              border: `1px solid ${theme.accentColor}`,
              borderRadius: '8px',
            },
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase': {
              color: theme.backgroundColor,
              '&.Mui-checked': {
                color: theme.accentColor,
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.accentColor,
                  opacity: 0.5,
                },
              },
            },
            '& .MuiSwitch-track': {
              backgroundColor: theme.textColor,
              opacity: 0.7,
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: theme.accentColor,
            },
          },
        },
      },
    },
  }
}

export {
  parseJwt,
  canCreateSurvey,
  canChangeLiveSurveyEndDate,
  canAnalyze,
  getMatSites,
  getProducts,
  getTheming,
  getLocalDate,
}
